import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import ProgressDashes from '../../components/ProgressDashes';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { getTopPurchases } from '../../utils/purchaseAnalytics';
import { getMostFrequentMerchant } from '../../utils/merchantAnalytics';
import { getMonthlySpending } from '../../utils/monthlyAnalytics';
import { getMostFrequentShoppingHour } from '../../utils/timeAnalytics';
import { getTopCategories } from '../../utils/categoryAnalytics';
import { getMostExpensiveDay } from '../../utils/dayAnalytics';
import { getTopStreaks } from '../../utils/streakAnalytics';
import { FavoriteTimeBox } from '../../components/FavoriteTimeBox';
import { TopPurchaseBox } from '../../components/TopPurchaseBox';
import { MonthlySpendingBox } from '../../components/MonthlySpendingBox';
import { RideOrDieBox } from '../../components/RideOrDieBox';
import { SpendingCategoriesBox } from '../../components/SpendingCategoriesBox';
import { ExpensiveDayBox } from '../../components/ExpensiveDayBox';
import { TopStreaksBox } from '../../components/TopStreaksBox';
import { SpendingMiniBox } from '../../components/SpendingMiniBox';
import { RealityCheckMiniBox } from '../../components/RealityCheckMiniBox';
import MoneyPersonalityBox from '../../components/MoneyPersonalityBox';
import { SpendingInsightMiniBox } from '../../components/SpendingInsightMiniBox';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const MoneyWrappedPage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  // Calculate all insights using the same logic as individual pages
  const topPurchase = useMemo(() => 
    transactions && transactions.length > 0 ? getTopPurchases(transactions)[0] : null
  , [transactions]);

  // Get monthly spending data
  const monthlySpending = useMemo(() => {
    if (!transactions || !transactions.length) return [];
    const data = getMonthlySpending(transactions);
    return Array.isArray(data) ? data : [];
  }, [transactions]);

  const rideOrDie = useMemo(() => 
    transactions && transactions.length > 0 ? getMostFrequentMerchant(transactions) : null
  , [transactions]);

  const timeStats = useMemo(() => 
    transactions && transactions.length > 0 ? getMostFrequentShoppingHour(transactions) : null
  , [transactions]);

  const topCategories = useMemo(() => 
    transactions && transactions.length > 0 ? getTopCategories(transactions) : []
  , [transactions]);

  // Calculate income and spending
  const { income, spending } = useMemo(() => {
    if (!transactions || transactions.length === 0) return { income: 0, spending: 0 };

    const totalIncome = transactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const totalSpending = Math.abs(transactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0));

    return { income: totalIncome, spending: totalSpending };
  }, [transactions]);

  const expensiveDay = useMemo(() => {
    if (!transactions || transactions.length === 0) return null;

    // Group transactions by date and only include spending (negative amounts)
    const dailySpending = transactions
      .filter(t => t.amount < 0)
      .reduce((acc, transaction) => {
        const date = new Date(transaction.date).toISOString().split('T')[0];
        if (!acc[date]) {
          acc[date] = 0;
        }
        acc[date] += Math.abs(transaction.amount);
        return acc;
      }, {} as { [key: string]: number });

    // Find the date with maximum spending
    let maxDate = '';
    let maxAmount = 0;

    Object.entries(dailySpending).forEach(([date, amount]) => {
      if (amount > maxAmount) {
        maxDate = date;
        maxAmount = amount;
      }
    });

    if (!maxDate) return null;

    const date = new Date(maxDate);
    const dayName = date.toLocaleString('en-US', { weekday: 'long' });
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    return {
      date,
      amount: maxAmount,
      dayName,
      isWeekend
    };
  }, [transactions]);

  const streaks = useMemo(() => 
    transactions && transactions.length > 0 ? getTopStreaks(transactions) : []
  , [transactions]);

  const topStreak = useMemo(() => 
    streaks && streaks.length > 0 ? streaks[0] : null
  , [streaks]);

  // Calculate monthly data for spending graph
  const monthlyData = useMemo(() => {
    if (!monthlySpending || monthlySpending.length === 0) return [];
    
    // Get last 6 months of data, ensuring we have valid data
    const validMonths = monthlySpending
      .filter(month => month && month.month && typeof month.total === 'number')
      .slice(-6)
      .map(month => {
        try {
          return {
            month: new Date(month.month).toLocaleString('default', { month: 'short' }),
            spending: month.total,
            income: 5000 // Replace with actual monthly income
          };
        } catch (e) {
          return null;
        }
      })
      .filter(month => month !== null);

    return validMonths;
  }, [monthlySpending]);

  // Reality check ratios (static data)
  const realityCheckRatios = {
    historicalRatio: 1.2,
    currentRatio: 11
  };

  // Early return if data isn't ready
  if (!transactions || transactions.length === 0) {
    return (
      <div className="min-h-screen bg-[#221A51]/[0.07] flex items-center justify-center">
        <div className="text-[#221A51] text-lg">
          No transaction data available. Please upload your transactions first.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative overflow-hidden">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        {/* Progress dashes */}
        <ProgressDashes currentStep={11} totalSteps={11} />
      </div>
      
      <div className="container max-w-4xl mx-auto px-4 py-6 sm:py-8 mt-4 sm:mt-8">
        <div className="space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="text-center space-y-1 sm:space-y-2">
            <h1 className="text-3xl sm:text-4xl font-black text-[#221A51]">
              Your Money Wrapped
            </h1>
            <p className="text-sm sm:text-base text-[#221A51]/60">
              Here's a snapshot of your spending habits
            </p>
          </div>

          {/* Money Personality - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-full"
          >
            <MoneyPersonalityBox />
          </motion.div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Column 1 */}
            <div className="space-y-3 sm:space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <SpendingInsightMiniBox
                  income={income}
                  spending={spending}
                />
              </motion.div>

              {topPurchase && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <TopPurchaseBox purchase={topPurchase} />
                </motion.div>
              )}

              {monthlySpending && monthlySpending.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <MonthlySpendingBox monthlySpending={monthlySpending} />
                </motion.div>
              )}

              {topCategories && topCategories.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <SpendingCategoriesBox categories={topCategories.slice(0, 3)} />
                </motion.div>
              )}
            </div>

            {/* Column 2 */}
            <div className="space-y-3 sm:space-y-4">
              {rideOrDie && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <RideOrDieBox merchantStats={rideOrDie} />
                </motion.div>
              )}

              {timeStats && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <FavoriteTimeBox
                    hour={timeStats.hour}
                    ampm={timeStats.ampm}
                    count={timeStats.count}
                  />
                </motion.div>
              )}

              {expensiveDay && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 }}
                >
                  <ExpensiveDayBox dayStats={expensiveDay} />
                </motion.div>
              )}

              {topStreak && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 }}
                >
                  <TopStreaksBox
                    streak={{
                      merchant: topStreak.merchant,
                      days: topStreak.length
                    }}
                  />
                </motion.div>
              )}
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {monthlyData && monthlyData.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <SpendingMiniBox monthlyData={monthlyData} />
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <RealityCheckMiniBox
                historicalRatio={realityCheckRatios.historicalRatio}
                currentRatio={realityCheckRatios.currentRatio}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoneyWrappedPage;
